import z from "zod";
import { ToolCallActionSchema } from "./actions";
import { MemoryRegistrySchema, ToolsRegistrySchema } from "./registries";

export const ChatRoleSchema = z.enum(['user', 'assistant', 'system', 'tool']);
export type ChatRole = z.infer<typeof ChatRoleSchema>;

export const ChatAttachmentSchema = z.object({
  name: z.string(),
  type: z.string(),
  data: z.string(),
});
export type ChatAttachment = z.infer<typeof ChatAttachmentSchema>;

export const ChatMessageSchema = z.object({
  role: ChatRoleSchema,
  content: z.string(),
  toolCalls: z.array(ToolCallActionSchema).nullish(),
  name: z.string().optional(),
  toolCallId: z.string().optional(),
  attachments: z.array(ChatAttachmentSchema).optional(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatCitationSchema = z.object({
    title: z.string(),
    sourceUrl: z.string(),
    excerpt: z.string(),
});
export type ChatCitation = z.infer<typeof ChatCitationSchema>;

export const ChatStateSchema = z.object({
    blockedActions: z.array(z.object({
        callId: z.string(),
        capabilityId: z.string(),
        argsString: z.string(),
        dependencies: z.array(z.string()),
    })).optional(),
    actionResults: z.record(z.string(), z.any()).optional(),
    pendingPreparedActions: z.array(z.object({
        callId: z.string(),
        actionId: z.string(),
        preparedId: z.string(),
        confirmationId: z.string().optional(),
        expiresAt: z.string(), // ISO date string
    })).optional(),
});
export type ChatState = z.infer<typeof ChatStateSchema>;

export const ChatUsageSchema = z.object({
    promptTokens: z.number(),
    completionTokens: z.number(),
    toolsCalled: z.array(z.object({
        name: z.string(),
        label: z.string(),
    })).optional(),
});
export type ChatUsage = z.infer<typeof ChatUsageSchema>;

export const ChatResponseSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('tool_calls'),
        assistantMessage: ChatMessageSchema,
        actions: z.array(ToolCallActionSchema),
        stateUpdate: ChatStateSchema.optional(),
        usage: ChatUsageSchema.optional(),
    }),
    z.object({
        type: z.literal('text'),
        assistantMessage: ChatMessageSchema,
        content: z.string(),
        citations: z.array(ChatCitationSchema),
        stateUpdate: ChatStateSchema.optional(),
        usage: ChatUsageSchema.optional(),
    }),
]);
export type ChatResponse = z.infer<typeof ChatResponseSchema>;

export const ChatStreamChunkSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('content'),
    content: z.string(),
  }),
  z.object({
    type: z.literal('tool_calls'),
    assistantMessage: ChatMessageSchema,
    actions: z.array(ToolCallActionSchema),
  }),
  z.object({
    type: z.literal('text'),
    assistantMessage: ChatMessageSchema,
    content: z.string(),
    citations: z.array(ChatCitationSchema),
  }),
  z.object({
    type: z.literal('error'),
    message: z.string(),
  }),
  z.object({
    type: z.literal('tool_result'),
    toolCallId: z.string(),
    name: z.string(),
    result: z.string(),
  }),
  z.object({
    type: z.literal('state_update'),
    state: ChatStateSchema,
  }),
  z.object({
    type: z.literal('usage_update'),
    usage: ChatUsageSchema,
  }),
]);
export type ChatStreamChunk = z.infer<typeof ChatStreamChunkSchema>;
export type ToolCallChunk = Extract<ChatStreamChunk, { type: 'tool_calls' }>;

export const AssistantRequestContextSchema = z.object({
  origin: z.string().optional(), // 'web', 'erp', etc.
});
export type AssistantRequestContext = z.infer<typeof AssistantRequestContextSchema>;

export const ChatRequestSchema = z.object({
  conversationId: z.string().optional(),
  messages: z.array(ChatMessageSchema),
  requestContext: AssistantRequestContextSchema.optional(),
  appContext: MemoryRegistrySchema.optional().default({}),
  tools: ToolsRegistrySchema.optional().default({}),
  state: ChatStateSchema.optional(),
});
export type ChatRequest = z.infer<typeof ChatRequestSchema>;

export const ChatTitleRequestSchema = z.object({
  message: z.string(),
  assistantResponse: z.string().optional(),
});
export type ChatTitleRequest = z.infer<typeof ChatTitleRequestSchema>;

export const ChatTitleResponseSchema = z.object({
  title: z.string(),
});
export type ChatTitleResponse = z.infer<typeof ChatTitleResponseSchema>;

export const ChatConversationSchema = z.object({
    id: z.string(),
    title: z.string(),
    messages: z.array(ChatMessageSchema),
    updatedAt: z.number(),
    pendingPreparedActions: z.array(z.object({
        callId: z.string(),
        actionId: z.string(),
        preparedId: z.string(),
        confirmationId: z.string().optional(),
        expiresAt: z.string(), // ISO date string
    })).optional(),
    blockedActions: z.array(z.object({
        callId: z.string(),
        capabilityId: z.string(),
        argsString: z.string(),
        dependencies: z.array(z.string()),
    })).optional(),
    actionResults: z.record(z.string(), z.any()).optional(),
});
export type ChatConversation = z.infer<typeof ChatConversationSchema>;

export const CreateChatConversationSchema = z.object({
    id: z.string(),
    title: z.string(),
    messages: z.array(ChatMessageSchema),
});
export type CreateChatConversation = z.infer<typeof CreateChatConversationSchema>;

export const UpdateChatConversationSchema = z.object({
    title: z.string().optional(),
    messages: z.array(ChatMessageSchema).optional(),
    pendingPreparedActions: z.array(z.object({
        callId: z.string(),
        actionId: z.string(),
        preparedId: z.string(),
        confirmationId: z.string().optional(),
        expiresAt: z.string(), // ISO date string
    })).optional().nullable(),
    blockedActions: z.array(z.object({
        callId: z.string(),
        capabilityId: z.string(),
        argsString: z.string(),
        dependencies: z.array(z.string()),
    })).optional().nullable(),
    actionResults: z.record(z.string(), z.any()).optional().nullable(),
});
export type UpdateChatConversation = z.infer<typeof UpdateChatConversationSchema>;